import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button, Card, Input, Label } from '../design-system';
import { login as loginApi, resolveTenant, TenantPublicInfo } from '../services/consentApi';
import '../design-system/styles.css';

interface SignInFormData {
  email: string;
  password: string;
}

/**
 * Tenant-specific authentication page.
 * Route: /t/:tenantSlug/login
 * 
 * Users are invited by tenant admin - no signup option here.
 */
const TenantAuthPage: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  
  const [tenant, setTenant] = useState<TenantPublicInfo | null>(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<SignInFormData>({
    email: '',
    password: '',
  });
  
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Resolve tenant on mount
  useEffect(() => {
    const fetchTenant = async () => {
      if (!tenantSlug) {
        setTenantError('No tenant specified');
        setTenantLoading(false);
        return;
      }
      
      try {
        const tenantInfo = await resolveTenant(tenantSlug);
        setTenant(tenantInfo);
        setTenantError(null);
      } catch (err) {
        setTenantError('Organization not found. Please check the URL and try again.');
      } finally {
        setTenantLoading(false);
      }
    };
    
    fetchTenant();
  }, [tenantSlug]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!formData.email.trim() || !formData.password.trim()) {
      setError('Please enter both email and password');
      return;
    }
    
    if (!tenant) {
      setError('Tenant not loaded');
      return;
    }
    
    setLoading(true);
    
    try {
      const data = await loginApi({
        tenantSlug: tenantSlug,
        email: formData.email.trim(),
        password: formData.password,
      });
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('authToken', data.token);
      
      const userInfo = data.user || {
        userId: data.userId,
        name: data.name,
        email: data.email,
        role: data.role,
        tenantId: data.tenantId,
        tenantSlug: data.tenantSlug || tenantSlug,
      };
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      
      // Navigate to tenant-specific dashboard if we have a tenant slug
      const finalSlug = data.tenantSlug || userInfo.tenantSlug || tenantSlug;
      if (finalSlug) {
        navigate(`/t/${finalSlug}/dashboard`);
      } else {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  }, [formData, tenant, tenantSlug, navigate]);

  // Loading state
  if (tenantLoading) {
    return (
      <main className="ds-container" style={{ textAlign: 'center', paddingTop: 100 }}>
        <p className="ds-text-muted">Loading organization...</p>
      </main>
    );
  }

  // Error state - tenant not found
  if (tenantError || !tenant) {
    return (
      <main className="ds-container">
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <Card>
            <div className="ds-alert ds-alert--error" role="alert">
              {tenantError || 'Organization not found'}
            </div>
            <p className="ds-text-muted" style={{ marginTop: 16 }}>
              Please check the URL and try again, or contact your organization administrator.
            </p>
            <div style={{ marginTop: 16 }}>
              <Link to="/login" style={{ color: 'var(--ds-accent)' }}>
                Go to general login page
              </Link>
            </div>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="ds-container" aria-labelledby="auth-title">
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <Card>
          {/* Tenant branding header */}
          <header className="ds-card__header" style={{ alignItems: 'center', flexDirection: 'column', textAlign: 'center' }}>
            {tenant.logo && (
              <img 
                src={tenant.logo} 
                alt={`${tenant.name} logo`} 
                style={{ height: 48, marginBottom: 16 }}
              />
            )}
            <h1 id="auth-title" className="ds-heading-xl" style={{ marginBottom: 4 }}>
              {tenant.name}
            </h1>
            <p className="ds-text-muted" style={{ margin: 0 }}>
              Sign in to your account
            </p>
          </header>

          {/* Error message */}
          {error && (
            <div className="ds-alert ds-alert--error" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          <form className="ds-form" onSubmit={handleSubmit} noValidate>
            <div className="ds-field">
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="you@company.com"
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="ds-field">
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
            </div>

            <Button type="submit" variant="primary" isLoading={loading} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <footer className="ds-card__footer" style={{ textAlign: 'center' }}>
            <p className="ds-text-muted" style={{ margin: 0 }}>
              Forgot your login URL?{' '}
              <Link to="/login" style={{ color: 'var(--ds-accent)' }}>
                Use general login
              </Link>
            </p>
          </footer>
        </Card>
      </div>
    </main>
  );
};

export default TenantAuthPage;
